import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { VariableSizeList } from 'react-window';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  SelectAll as SelectAllIcon,
} from '@mui/icons-material';
import { CreateProductInput } from '@/services/product-catalogue/pc-api';
import ProductReviewCard from './components/product-review-card';
import { validateProduct } from './components/validation-alerts';
import { tokens } from "@/tokens";

interface BatchProductReviewProps {
  products: CreateProductInput[];
  onUpdate: (updatedProducts: CreateProductInput[]) => void;
  selectedProducts: boolean[];
  onSelectedProductsChange: (selected: boolean[]) => void;
}

const COLLAPSED_ROW_HEIGHT = 160;
const EXPANDED_ROW_HEIGHT = 820;
const EXPANDED_ROW_HEIGHT_WITH_METAL = 960;
const LIST_HEIGHT = 600;

const BatchProductReview: React.FC<BatchProductReviewProps> = ({
  products,
  onUpdate,
  selectedProducts,
  onSelectedProductsChange,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const listRef = useRef<VariableSizeList>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width for the list
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Reset list size cache when products change
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [products.length]);

  const getItemSize = useCallback((index: number) => {
    if (!expandedItems.has(index)) return COLLAPSED_ROW_HEIGHT;
    const product = products[index];
    const hasMetalSection = product?.metalPercentage !== undefined;
    return hasMetalSection ? EXPANDED_ROW_HEIGHT_WITH_METAL : EXPANDED_ROW_HEIGHT;
  }, [expandedItems, products]);

  const handleToggleExpand = useCallback((index: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
    // Defer resetAfterIndex so state has updated before recalculating
    requestAnimationFrame(() => {
      listRef.current?.resetAfterIndex(index);
    });
  }, []);

  // Validation statistics
  const validationStats = useMemo(() => {
    let validCount = 0;
    let errorCount = 0;
    let warningCount = 0;

    products.forEach(product => {
      const issues = validateProduct(product);
      const hasErrors = issues.some(issue => issue.type === 'error');
      const hasWarnings = issues.some(issue => issue.type === 'warning');

      if (hasErrors) {
        errorCount++;
      } else if (hasWarnings) {
        warningCount++;
      } else {
        validCount++;
      }
    });

    return { validCount, errorCount, warningCount };
  }, [products]);

  const selectedCount = selectedProducts.filter(Boolean).length;

  const handleProductUpdate = useCallback((index: number, updatedProduct: CreateProductInput) => {
    const newProducts = [...products];
    newProducts[index] = updatedProduct;
    onUpdate(newProducts);
  }, [products, onUpdate]);

  const handleProductRemove = useCallback((index: number) => {
    const newProducts = products.filter((_, i) => i !== index);
    const newSelected = selectedProducts.filter((_, i) => i !== index);

    // Clean up expanded state: remove the deleted index, shift higher indices down
    setExpandedItems(prev => {
      const next = new Set<number>();
      prev.forEach(i => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });

    onUpdate(newProducts);
    onSelectedProductsChange(newSelected);
  }, [products, selectedProducts, onUpdate, onSelectedProductsChange]);

  const handleSelectAll = () => {
    const allSelected = selectedProducts.every(Boolean);
    onSelectedProductsChange(new Array(products.length).fill(!allSelected));
  };

  // Row renderer for VariableSizeList
  const Row = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => (
    <div style={{ ...style, paddingRight: 4 }}>
      <ProductReviewCard
        product={products[index]}
        index={index}
        onUpdate={handleProductUpdate}
        onRemove={handleProductRemove}
        isSelected={selectedProducts[index]}
        requiresMetalPercentage={false}
        expanded={expandedItems.has(index)}
        onToggleExpand={handleToggleExpand}
      />
    </div>
  ), [products, selectedProducts, expandedItems, handleProductUpdate, handleProductRemove, handleToggleExpand]);

  // Cast to work around react-window / @types/react version mismatch
  const ListComponent = VariableSizeList as unknown as React.ComponentType<any>;

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Review Products
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.text.secondary }} gutterBottom>
          Review and edit your products before uploading. Fix any errors and warnings.
        </Typography>

        {/* Statistics */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${validationStats.validCount} Valid`}
              color="success"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<WarningIcon />}
              label={`${validationStats.warningCount} Warnings`}
              color="warning"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              icon={<ErrorIcon />}
              label={`${validationStats.errorCount} Errors`}
              color="error"
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Chip
              label={`${selectedCount}/${products.length} Selected`}
              color="primary"
              variant="outlined"
            />
          </Grid>
        </Grid>

        {/* Batch Actions */}
        <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedProducts.every(Boolean)}
                indeterminate={selectedProducts.some(Boolean) && !selectedProducts.every(Boolean)}
                onChange={handleSelectAll}
              />
            }
            label="Select All"
          />

          <Button
            variant="outlined"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            size="small"
          >
            {selectedProducts.every(Boolean) ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {validationStats.errorCount > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please fix all errors before uploading. Products with errors cannot be created.
          </Alert>
        )}
      </Paper>

      {/* Virtualized Product List */}
      <Box ref={containerRef} sx={{ mb: 3 }}>
        {containerWidth > 0 && (
          <ListComponent
            ref={listRef}
            height={LIST_HEIGHT}
            itemCount={products.length}
            itemSize={getItemSize}
            width={containerWidth}
            overscanCount={3}
          >
            {Row}
          </ListComponent>
        )}
      </Box>
    </Box>
  );
};

export default BatchProductReview;
